import { FormattingRuleLabel, InvertCheckbox } from './atoms';
import { APIEntityType } from '@ardoq/api-types';
import { AsyncSelect } from '@ardoq/select';
import { GhostButton, DragButton } from '@ardoq/button';
import { DeleteIcon } from '@ardoq/icons';
import { FormattingRuleGroupBox } from '../../../atoms';
import { FlexBox } from '@ardoq/layout';
import { TagFormattingRule } from '../types';
import { useFormattingContext } from '../useFormattingContext';
import { t } from '@ardoq/i18n';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { RuleNotificationType } from '@ardoq/query-builder';
import { WarningNotificationRule } from '../../../WarningNotificationRule';
import { perspectivesRowDragAndDropHandlers } from '../../../perspectivesRowDragAndDropHandlers';
import { FormattingColorPicker } from '../../FormattingColorPicker';
import { buildColorWarningKey } from '../colorWarnings';
import { ValueWarningMessage } from './ValueWarningMessage';
import { FORMATTING_RULE_LINE_HEIGHT as LINE_HEIGHT } from '../../../consts';

type FormattingRuleByTagProperties = {
  formattingRule: TagFormattingRule;
};
export const FormattingRuleRowByTag = ({
  formattingRule: rule,
}: FormattingRuleByTagProperties) => {
  const {
    updateRule,
    removeRule,
    asyncSuggestionsLoaders,
    tagsFormattingGroups,
    getRuleNotificationParams,
    ruleInfo,
    i18nLocale,
  } = useFormattingContext();
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      rule.__id || null,
      useDragAndDropContext()
    );

  const { message, notificationType } = getRuleNotificationParams?.(rule) || {};
  const isWarning = notificationType === RuleNotificationType.WARNING;

  if (isWarning) {
    return (
      <WarningNotificationRule
        message={message}
        removeRule={() => removeRule(rule.__id)}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        dataTestId="group-box-formatting-warning-notification"
      />
    );
  }
  const { colorWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarning = colorWarnings[buildColorWarningKey(rule.color)];

  const isDraggable = !!rule.__id;

  return (
    <FormattingRuleGroupBox
      data-testid="group-box-formatting-rule-by-tag"
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <DragButton
        draggable={isDraggable}
        style={{ lineHeight: `${LINE_HEIGHT}px` }}
        i18nLocale={i18nLocale}
      />
      <FormattingRuleLabel ruleType={APIEntityType.TAG} />
      <FlexBox gap="small" flex={1} />
      <FlexBox gap="small" flex={1} />
      <FlexBox flexDirection="column" gap="small" flex={2}>
        <FlexBox gap="small" flex={1}>
          <AsyncSelect
            dataTestId="tag-formatting-rule-select"
            loadOptions={asyncSuggestionsLoaders.loadTagSuggestionsAsync}
            defaultOptions={tagsFormattingGroups}
            placeholder={t(
              i18nLocale,
              'perspectives:formattingRuleRow.typeToSearch',
              'Type to search...'
            )}
            value={rule.value ? { value: rule.value, label: rule.value } : null}
            onChange={selectedOption => {
              if (!selectedOption) return;
              const { value } = selectedOption;
              updateRule(rule.__id, { value });
            }}
            hasWarning={Boolean(colorWarning)}
          />
        </FlexBox>

        <ValueWarningMessage message={colorWarning} />
      </FlexBox>
      <InvertCheckbox
        isInverted={rule.isInverted}
        onChange={isInverted => updateRule(rule.__id, { isInverted })}
      />
      <FormattingColorPicker
        dataTestId="tag-formatting-rule-color-picker"
        value={rule.color}
        onValueChange={color => updateRule(rule.__id, { color })}
        isNewConditionalFormatting={false}
      />
      <GhostButton
        dataTestId="remove-tag-formatting-rule-button"
        onClick={() => removeRule(rule.__id)}
      >
        <DeleteIcon />
      </GhostButton>
    </FormattingRuleGroupBox>
  );
};
